import {
  InlandMarineTypes,
  FarmRiskType,
  LiabilityType,
  lookupsJson,
  FarmScheduleCoverageType,
  StructureCategory,
  cfmMinimumRateableCoverageC,
  earthquakeZonesByZipCfmMO,
  standardizedCancelNonRenewalReasonCodes
} from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

import farmUWQuestions from './cfm/farm-uw-questions';
import autoUWQuestions from './cfm/auto-uw-questions';

const {
  scheduledFarmPropertyCategories,
  agriculturalConstructionType,
  deductibleAllOther: allOtherOptions
} = lookupsJson.farm;
const allConstructionTypeIds = agriculturalConstructionType.map((ct) => ct.id);

// CFM spec sheet (issue #31667): inland marine all-peril deductible options are
// 250/500/1000/2500/5000 — no $1,500 and no $10,000.
const cfmInlandMarineDeductibleOptions = allOtherOptions.filter((option) =>
  [250, 500, 1000, 2500, 5000].includes(option.id)
);

// Minimum All-Peril deductible tied to the Coverage A limit for the dwelling forms (FO1/FO2/FO3),
// per the CFM spec sheet (issue #32441). The farm-shared validation schema reads
// businessRules.deductibleAllOther.minByCoverageA and blocks a deductible below the highest matching
// band. FO4 (renters/contents, no Coverage A) is not gated.
const cfmMinAllPerilDeductibleByCoverageA = [
  { minCoverageA: 250000, minDeductible: 2500 },
  { minCoverageA: 500000, minDeductible: 5000 },
  { minCoverageA: 750000, minDeductible: 10000 }
];

const dwellingInlandMarineItemTypes = [
  InlandMarineTypes.Atv,
  // #34019: Gator/Utility Transport Vehicle — mirrors ATV (rates the same, feeds incidental liability).
  InlandMarineTypes.GatorUtilityTransportVehicle,
  InlandMarineTypes.BoatDock,
  InlandMarineTypes.Boats,
  InlandMarineTypes.CoinCollection,
  InlandMarineTypes.FineArts,
  InlandMarineTypes.Furs,
  InlandMarineTypes.GolfCarts,
  InlandMarineTypes.GolfEquipment,
  InlandMarineTypes.Guns,
  InlandMarineTypes.Jewelry,
  InlandMarineTypes.LawnMowers,
  InlandMarineTypes.MiscellaneousProperty,
  InlandMarineTypes.MusicalInstruments,
  InlandMarineTypes.PersonalTools,
  InlandMarineTypes.Silverware,
  InlandMarineTypes.SportingEquipment,
  InlandMarineTypes.Tack,
  InlandMarineTypes.UnmannedAircraft
].sort((a, b) => a.id.localeCompare(b.id));

const config = {
  spinner: 'ember',
  // New-look colors (#33148), built by core/components/theme/reskin-palette.js. Prototype values from
  // CFM's account-fe palette (navy, gray text, light gray surfaces, control gray); design to confirm.
  reskinPalette: {
    seeds: { primary: '#123257', secondary: '#545759', accent: '#123257', surface: '#F0F0F0', error: '#7A0027' },
    overrides: { beige_60: '#E6E7E8' }
  },
  // How long the newer-offer interstitial dwells before it redirects (#30994). It is the only
  // window staff have to reach "Keep version" and stay on the offer they opened, so CFM doubles it to 10s — its agents asked for longer to react.
  offerRedirectInterstitialMs: 10000,
  // Issue #33591: standardized cancellation / non-renewal reasons shared across the mutuals.
  // The ordered ids turn on the matching `optIn` entries from the `cancelReason` superset.
  // Issue #33913: append the legacy "Policy Rewrite" (CNRW) reason so mutuals get the same rewrite
  // flow as Branch/Bix. Selecting CNRW flat-cancels the term and surfaces the "Rewrite Policy" button
  // (isPolicyEligibleForRewrite keys off acceptableCancelCodesForRewrite = ['CNRW']). CNRW is
  // permission-gated on canRewritePolicy in cancellation-form.jsx, so only rewrite-capable users see it.
  // Issue #35987: NACP ('Policy Not Accepted') brings the mutuals to parity with Branch/BIX. It is a
  // plain (non-optIn) cancellation reason in the superset, so it resolves into the CANCEL dropdown
  // (not non-renewal) and stops a cancelled no-renew policy being treated as a fresh cancel reason.
  cancelNonRenewalReasons: [...standardizedCancelNonRenewalReasonCodes, 'CNRW', 'NACP'],
  // #36221: initial state of the "Refund credit card fee" cancellation checkbox. false = keep the
  // card-processing fee for the payments already made (the default for every mutual); set true only
  // where compliance requires the fee to be refunded. Must match the backend default in
  // packages/brand-config-be (fees.farm.refundCreditCardFeeOnCancellation).
  refundCreditCardFeeOnCancellationByDefault: false,
  // #36240: staff cancels need this notice unless insured-initiated; mirrors brand-config-be (enforced in policy-mod).
  cancellationNoticeDays: 15,
  // #37501: a refund is always processed with a delay, never sent immediately (brand-config-be
  // billingAutomation.delayRefunds, enforced in policy-mod). The policy-change preview shows this on a refund.
  delayedRefundNote: 'Refunds for CFM policies are processed with a delay.',
  // #36240: the reinstate form shows "Ignore fee" (fee applies unless ticked) instead of "Apply Fee".
  reinstatementIgnoreFeeCheckbox: true,
  // #36919: when the nightly renewals cron generates a renewal, for the policy header's "renewal will
  // generate" dates. Must match brand-config-be automatedRenewalWindow, which the cron runs on.
  automatedRenewalWindow: {
    maxDaysBeforeRenewal: 60,
    importedTermMaxDaysBeforeRenewal: 60,
    minDaysBeforeRenewal: 3,
    renewalEffectiveOnOrAfter: '2026-12-15'
  },
  // #37225: CFM's crons (renewals included) leave imported policies alone until this day. Must match
  // brand-config-be importedPolicyAutomationStartDate.
  importedPolicyAutomationStartDate: '2026-11-02',
  // #37684: what Staff calls the billing system imported policies came from, in the note on a balance billed there
  // before the cutover.
  importedLegacySystemName: 'CFMGo',
  featureFlags: {
    // #37040: on a policy migrated from GO, an item that came over in the migration may keep the fields
    // GO never carried (or carried in a shape EmberTech now rejects) without blocking an endorsement.
    // Policies written in EmberTech, and items added after migration, keep full validation.
    relaxMigratedCarryOverValidation: true,
    // #37629 (Joe: match Forreston's #37217): a structure that came over in the GO migration carries no RCE, so its RC
    // loss settlement doesn't need one ("RC loss settlement selected but RCE data not available"), even after its
    // Coverage E changes, at renewal and on later endorsements. Structures added after the migration still need one.
    migratedRcWithoutRce: true,
    // #37717: on the term imported from GO, a Barn/Outbuilding that came over as Building Class 1 keeps Class 1 although
    // it is now older than the Class 1 age limit, as GO keeps it until the renewal moves it to Class 2. Renewal terms,
    // new business and structures added or switched to Class 1 after the migration keep the age rule.
    keepMigratedClass1OverAge: true,
    // #37763 (Mutual Services): an FO-6 structure that came over from GO with a Coverage E below the building-class
    // minimum ($1,000, or $7,500 for Class 1), such as a $500 fence, keeps that limit on the imported term, at renewal
    // and on every later endorsement, and may not be lowered further. A structure an earlier endorsement stored keeps its
    // stored limit the same way; one added in the endorsement gets the minimum. The farm post-data rules agree
    // (coverageFloor). An underwriter's migrated-renewal exemption from the 80% floors covers only structures the term
    // arrived with.
    keepMigratedSubFloorCoverageE: true,
    // #37763 (Joe): such a structure also keeps its Coverage E when a later 360Value pull values it lower, above the
    // 100% RCE / ACV cap, and may not be raised further. Mirrors the Coverage A cap floor (#37133) and the farm
    // post-data rules' migratedCoverageECapFloor.
    keepMigratedCoverageEAboveCap: true,
    // #37717 (stakeholders: CFM on, Forreston off): the FO-6 class / age rules age a structure at the term start (the
    // term's effective year), as the CFM farm rater sends home_age. Forreston's and Oakwood's raters age in today's year,
    // so they keep today's-year ageing. Mirrors the CFM farm post-data rules' confinement/poultry RC age.
    ageFarmStructuresAtTermStart: true,
    // #37141: on a migrated policy, changing one of the seven 360Value dwelling fields (Year Built, sq ft, basement
    // sq ft, families, home quality, stories, construction type) asks for confirmation before it re-runs the
    // replacement cost estimate. Mirrors brand-config-be migrated360RerunRules.
    confirmMigrated360Rerun: true,
    // Incidents tab lists 3-5 year old home losses from homeClaimsForUW as not rated (#36608).
    // Branch home raters read only homeClaimDates; mutual raters can price on homeClaimsForUW.
    showNotRatedHomeClaims: false,
    // Staff "Create Payment Link": a 30-day pay-without-signing-in link to the account site (#36685).
    guestPaymentLinks: true,
    // Opening a rewritten (CNRW) original asks whether to go to the rewritten policy (#36661).
    policyRewrittenRedirect: true,
    // A customer can hold a FarmHome (Z) quote on one property and a FarmRental (Y) quote on
    // another. Deduping offer search on `webUserId` hid one of them, so agents kept opening and
    // editing the wrong quote (#34790). Dedupe on `offerSearchDedupeKey` instead — still one row
    // per quote, but per product set rather than per customer.
    //
    // ON: cfm-production's offer index was reindexed on 2026-09-03 under release
    // cfm-v2026-09-03-3 — the attribute is filterable, all 639 documents carry the key, and the
    // rebuild was lossless (639 in, 639 out). Verified against the live index before enabling:
    // `distinct: webUserId` returned 1 row for the reported customer, `distinct:
    // offerSearchDedupeKey` returned both their FarmHome and FarmRental quotes.
    //
    // Do not turn this on for a brand whose index has not been reindexed: until the documents
    // carry the key, MeiliSearch returns every one of them undeduped.
    offerSearchDedupesByProductSet: true,
    // Underwriters may override the system-pulled insurance score with a manually entered
    // value on the applicant/driver cards; the rater uses the manual score. CFM and Oakwood
    // rate on credit; Forreston does not, so the control is hidden there. (#34377)
    insuranceScoreOverride: true,
    // Lets an underwriter (CanApproveUWReview) manually override a property's fire protection class
    // via the pencil-edit control, capturing who + reason; the rater uses the override. Enabled for
    // the mutual brands (CFM, Forreston, Oakwood); off for Branch. (#35212)
    protectionClassOverride: true,
    // Descope-authority override that LOCKS Coverage A & Coverage E limits with a required internal
    // reason (#35617). On for the mutual brands; off for Branch. See branch.js for the full note.
    coverageLimitOverride: true,
    // The applicant's insurance score is CFM underwriting information, not something their agents
    // should be reading off the People tab — hide it from agency users on every person card that
    // shows it. Internal (non-agency) staff still see it. (#35046)
    hideInsuranceScoreFromAgents: true,
    insuranceScoreHelp: false,
    // "Total man months (per year)" under Employer's Liability is a Forreston-only rate-structure
    // input — CFM does not use it, so it is hidden in the UI and dropped from the CFM DocGen
    // template (#33964).
    hideFarmManMonths: true,
    // CFM allows any number of domestic employees (premium is charged on the excess over 2),
    // unlike Forreston which caps the count at 2. (#33973)
    unlimitedDomesticEmployees: true,
    // Farm quotes require applicant date of birth and phone at new offer.
    applicantDateOfBirthAtNewOffer: true,
    phoneRequired: true,
    // Letter of Experience generator is Branch-only for now (#29700) — off until the mutual
    // letter format is confirmed.
    generateLoe: false,
    // Auto-exclude cosmetic damage on farm risks at offer creation.
    shouldAutoExcludeCosmeticDamage: true,
    // The staff "Quote PDF" on a standalone Auto offer renders through the Zywave DocGen pipeline
    // (generateApplicationDraft, docContext 'quote' — CFM's own filed template, slot 24345, #34154)
    // instead of the Branch-only DocRaptor screenshot of the customer /review page, which this
    // deploy cannot serve (no PDF-provider credentials, no customer quote site) and which failed
    // with "Cannot return null for non-nullable type" (#35921). Mirrors brand-config-be's
    // `autoDocGenTemplateId`; read by offer/helpers/quote-pdf-pipeline.js.
    autoQuotePdfViaDocGen: true,
    // #35739: underwriters (CanApproveUWReview) may override the locked Cosmetic Damage Exclusion per
    // risk (dwellings and farm structures), in either direction; the choice persists and renews.
    cosmeticDamageExclusionUwOverride: true,
    // Underwriters (CanDeleteInternalDocuments) may delete offer Supporting Documents; mutual-only,
    // off on Branch. (#34202)
    deleteSupportingDocuments: true,
    // "Suppress communications" on the endorsement confirm, internal staff only (#37246).
    suppressEndorsementCommunications: true,
    // Staff "Send Invoice" billing action; mutual-only, off on Branch. (#34203, #34419)
    sendInvoice: true,
    // Agent payment presets + staff sweep charges for a requesting agent (#36683). CFM/Forreston only.
    agentPaymentOptions: true,
    // Billing holds stop only automated billing and cancellation; manual payments always go through (#37417).
    manualPaymentsThroughBillingHold: true,
    // Staff invoice history on the Customer page; mutual-only, off on Branch. (#36679)
    invoiceHistory: true,
    // Offer search results redesign (#36395): Branch only for v1.
    offerSearchRedesign: false,
    // Farm agents may have a personal mailing address in a state they don't write in;
    // the licensed-state check is enforced per farm location instead. (Issue #30876)
    enforceLicensedStateOnNamedInsured: false,
    // Show the agency Portal (Notion pages) to every user on this mutual, not just
    // agents/underwriters. (#31094, #33024)
    agencyPortalVisibleToAllUsers: true,
    // Display/edit the policy EXPIRATION date instead of the stored endDate ("Last Full Day Of
    // Coverage"), matching the legacy IMT presentation used by these mutual carriers. The stored
    // endDate is unchanged; showExpirationInsteadOfEndDate() reads this top-level flag. (#32729)
    showExpirationDateInsteadOfEndDate: true,
    // Adds the "Liability" option to the new-offer Risk Type dropdown (full Farm and Home,
    // not Rental): a form-only sentinel that submits the location with no risks and
    // includeFarmLiability on. (#33168, extended to Home by #34379)
    liabilityOnlyFarmQuoting: true,
    // CFM mono-line auto. CFM does not offer the Community Drive telematics program (default false).
    hideCommunityDrive: true,
    // CFM exposes a driver-level Extended Non-Owned Coverage toggle (default false).
    supportsExtendedNonOwnedCoverage: true,
    // CFM files a $250 comprehensive and collision deductible on cars, below Branch's $500 floor, so
    // the car card lets it be selected (#36152). Unset = the Branch minimums from @ourbranch/lookups.
    minimumAutoPhysicalDamageDeductible: 250,
    // CFM gates Transportation Expense (rental) on Comprehensive being selected (default false).
    transportationExpenseRequiresComprehensive: true,
    // CFM auto collects driver relationship-to-insured plus per-vehicle sole-ownership and, when a
    // vehicle isn't solely owned by the applicant, the joint owner (default false).
    supportsVehicleOwnershipDetails: true,
    // CFM auto rates on Verisk/RAPA symbols (a liability + a physical-damage pair), not the Branch
    // make/model/style scheme, so the make/model symbol inputs are always empty for it. Show the RAPA
    // symbols the rater keys on instead — including the rater's 'A' default for a symbol-less or
    // antique car, which would otherwise show as blank Branch inputs (#35042). Default false.
    autoUsesRapaSymbols: true,
    // CFM auto labels the Additional Custom Parts limit as "Custom Equipment Coverage", allows values
    // outside the standard dropdown tiers (migrated policies carry a broad range), and requires a
    // free-text description whenever the limit is greater than 0 (#35599). Default false.
    customEquipmentCoverage: true,
    // CFM auto assigns a driver rating class code from gender + driver status, so both must be
    // collected on every auto driver card regardless of the state gender hide-list (#35598, #35605).
    autoRequiresDriverGender: true,
    // CFM auto collects Driver Status (Principal / Occasional) — the driver_status the CFM ClarionDoor
    // rater keys the class code on (#35605). Default false.
    autoRequiresDriverStatus: true,
    // Highest Education is not a CFM auto rating factor, so hide it on auto driver cards (#35602).
    hideAutoHighestEducation: true,
    // CFM labels the trailer value "Stated Amount" — the filed program's term for the amount the
    // trailer is insured for (Guidewire's StatedAmt) (#35838). Default false.
    trailerStatedAmountLabel: true,
    // Trailer contents are not a CFM rating input (the ClarionDoor rater sends trailer_contents 0
    // when absent), so hide the Contents Value field on trailer cards (#35838). Default false.
    hideTrailerContents: true,
    // CFM collects a trailer's make and model (GO requires both; the Dec/App trailer row prints them)
    // (#36346). Default false — Branch trailers have never carried them.
    trailerRequiresMakeModel: true,
    // CFM has no rule that a trailer's comp/coll deductibles match an insured vehicle's, so skip the
    // Branch match rule on trailer cards (#36149). Default false.
    trailerDeductiblesIndependentOfVehicles: true,
    // No "Underwriting Changes for Renewal" on an original imported term (#36833): a mid-term IRPM
    // must not carry into the renewal; the renewal restores GO's adjustment. Mirrors brand-config-be's
    // blockRenewalChangesOnImportedTerms, which policy-mod enforces. Default false.
    blockRenewalChangesOnImportedTerms: true,
    // CFM auto Underwriting Adjustment: an underwriter-only "Policy-level Adjustment" section
    // writing auto.adjustmentFactor (the CFM ClarionDoor premium_modifier, default 1) and its
    // rationale (#35787). Default false.
    autoPolicyLevelAdjustment: true,
    // CFM farm policy-wide coverage rules (#33318): tiered agent/underwriter delete gating on
    // Equipment Breakdown, the $5,000–$25,000 Farm Extra Expense range, and the auto-included,
    // no-delete machinery coverages (Machinery Not Described, Newly Acquired Machinery, Rented or
    // Borrowed Equipment, Replacement Machinery). Off by default for other brands.
    farmPolicywideCoverageRules: true,
    // Agency sweep accounts (#33155): independent agents can pay from their agency's
    // Stripe account. On in non-prod and now in production too (#35085).
    sweepAccounts: { enabledInNonProd: true, enabledInProd: true },
    // Agency Reports (#36219): Branch-only until this brand has its own report bucket.
    agencyReports: { enabled: false, visibleToAgencies: false },
    // Underwriter MicroBilt-bypass on ACH adds (#33938): mutual-only, on in every env.
    microBiltVerificationBypass: true,
    // "Collect payment information later" at bind (#33891): mutual-only.
    collectPaymentLater: true,
    // Policy drafting (working drafts / draft-and-keep-changing endorsement flow).
    // Consumed via core/helpers/policy-drafting.js.
    policyDrafts: true,
    // Offer "Direct" as an auto payment method (#35123): migrated CFM auto policies are direct-billed,
    // so the payment-method select must list Direct (else a stored 'D' shows as the raw code).
    autoDirectBillPaymentMethod: true,
    // Show "Current Auto Details" on the new-offer form from the start for any policy type that
    // writes an auto line, instead of waiting for the rater to ask (code 5003). CFM agents quote
    // auto directly and kept getting stuck in the reveal-then-resubmit loop (#35337).
    showCurrentAutoDetailsUpFront: true,
    // Show the "Add Car" (VIN) section up front too (#35337): a monoline CFM auto with no VIN and a
    // CV no-hit otherwise returns an empty offer with no way to add a vehicle.
    showAddVehicleUpFront: true,
    // CFM never sold Renters, so the "Renters Coverage" section on the policy Details → Auto screen
    // is hidden — its agents were selecting it by mistake. Branch-only capability (#35383). Read via
    // core/helpers/renters-on-auto.js.
    offerRentersOnAuto: false,
    // CFM Auto must accompany a Home/Farm policy; show the "Companion Policy" section so staff can
    // record which one, for cross-reference (#35434). Informational only.
    collectAutoCompanionPolicy: true,
    // #35990: CFM auto non-rated individuals use a restricted Reason list (Excluded / Underage only).
    restrictNonRatedIndividualReasons: true,
    // CFM requires 6 months of prior coverage with the same carrier but never asks the applicant —
    // underwriting reads it off the prior carrier's report at approval — so the new-offer Current
    // Auto Details drops "Have you had auto coverage in the last 31 days?" (#36251). Default false.
    hideAutoCoverageInPast31Days: true,
    // CFM doesn't cancel a prior carrier's policy on the member's behalf: no "Stress - Free Switch"
    // section at checkout, for underwriters and agents alike (#36286). Default false.
    hideStressFreeSwitch: true,
    // The Companion Policy section above already records the CFM homeowner policy, so agents aren't
    // asked "also been sold through your agency?" again at checkout (#36287). Default false.
    skipCheckoutOpposingPolicyQuestion: true,
    // CFM auto interested parties are typed by the filed-form interest — Additional Insured - Lessor
    // (PA 1100), Loss Payee, Trustee (PA 1300), Additional Interest — with no "Lien Holder", one party
    // attachable to several vehicles, and trustee names on a Trustee (#36264). Read via
    // common/helpers/auto-interested-party-helpers.js. Default false.
    autoInterestedPartyTypes: true
  },
  draftablePolicyTypes: [policyTypes.Farm, policyTypes.FarmHome, policyTypes.FarmRental, policyTypes.Auto],
  // CFM effective date rules by role and transaction type — parity with Forreston (GitHub issue #29728).
  // Agents get bounded backdate/forward windows; underwriters (internal users) are unrestricted by omission.
  effectiveDateRules: {
    agent: {
      newBusiness: { maxBackdateDays: 5, maxForwardDays: 60 },
      endorsement: { maxBackdateDays: 30, maxForwardDays: 60 }
    }
    // Underwriters (internal users): no restrictions — handled by omission
  },
  // CFM cannot write new-business farm policies effective before its hard launch date, moved to
  // 9/1/2026 for the new-business go-live (#34458). FarmHome (Z, "Home") and FarmRental (Y) share
  // global.farmEffectiveDate, so this one value governs new-business Farm and Home alike. Backend
  // source of truth lives in packages/brand-config-be/src/brands/cfm.ts; keep these in sync.
  farmMinimumEffectiveDate: '2026-09-01',
  // CFM opens auto new business for policies effective 11/1/2026 or later, for agents and underwriters
  // alike (#37461). Read by the offer effective-date schema; backend source of truth lives in
  // packages/brand-config-be/src/brands/cfm.ts; keep these in sync.
  autoMinimumEffectiveDate: '2026-11-01',
  hiddenFields: [
    // NOTE: unlike Forreston, CFM keeps `applicant.insuranceScore` VISIBLE — CFM uses insurance score
    // in rating and intentionally diverges here. Only the demographic fields are hidden.
    'applicant.gender',
    'applicant.maritalStatus',
    'applicant.highestEducation'
  ],
  helpWidget: {
    showPhone: true,
    // #37458: the Staff "?" speed dial links to CFM's Liberate FNOL forms, one for property and one for auto.
    fnolLinks: [
      { label: 'File a Property Claim', url: 'https://cfm.ember.liberate.claims/fnol' },
      { label: 'File an Auto Claim', url: 'https://cfm.ember.liberate.claims/fnol-auto' }
    ]
  },
  // CFM auto underwriting questions (issue #32744, parent #32176). Presence of `auto.uwQuestions`
  // is what turns the auto UW sections on — no other brand populates it, so they render CFM-only.
  auto: {
    uwQuestions: autoUWQuestions
  },
  discounts: {
    hasFutureEffectiveDateDiscount: false,
    // Advance Quote Discount is off for farm mutuals (parity with Forreston/Oakwood). Without this the
    // consumer defaults it ON via `?? true` (use-brand-features.js), silently enabling it for CFM.
    hasAdvanceQuoteDiscount: false,
    hasCropInsuranceDiscount: true,
    hasTraditionsDiscount: true,
    hasAssociationDiscount: true,
    hasMultilineDiscount: true,
    hasPaperlessDiscount: false,
    // No Senior or Millennial discount on CFM rentals (Joe, 2026-10-21); mirrors brand-config-be
    // farmRentalAgeDiscounts, which the rater, the dec and the importer honour.
    hasFarmRentalAgeDiscounts: false,
    // Expose the selectable Auto/Home Bundle discount so agents can add it on CFM Home (Z), Rental
    // (Y), and full Farm (F) offers/policies — parity with Forreston. The discount lives in the
    // shared farm-discounts list and is otherwise hidden by the `?? false` default. Migrated
    // policies follow their stored global.multiPolicyDiscount independent of this flag. (#33595)
    hasAutoHomeBundleDiscount: true,
    // #33639: the Auto/Home bundle dropdown's middle option reads "Auto policy with <carrier>". CFM's
    // own auto product is CFM (Forreston/others default to BIX). Display-only — the stored id stays
    // 'BIX' so the rater's auto_homeYN level-1 mapping and migrated policies are unaffected.
    autoBundleCarrierLabel: 'CFM'
  },
  farm: {
    // CFM farm UW questions. Currently identical to the pre-brand-split base set; diverges as CFM
    // authors its own question tree (spreadsheet 14PWjPWr5AbY-zsf8F0w0dA-XMQN5jShH2rx3wozI99Q, #32176).
    uwQuestions: farmUWQuestions,
    MO: {
      coverageLimits: {
        // Minimum Coverage A floor for dwelling risks (GitHub issue #28995)
        minimumDwellingCoverageA: 1000,
        minimumElectedCoverageC: cfmMinimumRateableCoverageC,
        // Base pool limit for above-ground pools (GitHub issue #29233)
        aboveGroundPoolDefaultLimit: 2500
      },
      // Farm risk types CFM MO writes beyond the classic FO set (`baseFarmRiskTypes` in
      // @ourbranch/lookups). SOURCE OF TRUTH: `farm.MO.additionalRiskTypes` in
      // packages/brand-config-be/src/brands/cfm.ts (read there by Brand.getAllowedFarmRiskTypes);
      // this staff-FE copy must stay in sync, and cfm.test.js pins it. Standalone inland marine is
      // Farm (F) only — Home (Z) and Rental (Y) exclude it via their policyTypeMasks below.
      // Load-bearing: `InlandMarine` is in lookupsJson.farm.riskType, which every brand reads, and
      // the F masks set no allowedRiskTypes — without this opt-in the option would appear on
      // Forreston/Oakwood/Branch too. (#33164 — docs/farm-standalone-inland-marine.md)
      additionalRiskTypes: [FarmRiskType.InlandMarine],
      // Earthquake zone map for CFM MO (issue #32439). Data lives in @ourbranch/lookups so the
      // staff-FE and the BE brand config (packages/brand-config-be/src/brands/cfm.ts) share a
      // single source of truth for the ~160-row regulatory zip list. Zone 4 → read-only "No
      // coverage" + post-data-rule zeros persisted values; Zone 2 → soft rejection; unlisted MO
      // zips = Zone 1 (standard).
      earthquakeZonesByZip: earthquakeZonesByZipCfmMO,
      // Earthquake deductible options offered for CFM (issue #32439). Only "no coverage" (0) and 15%
      // may be elected. Non-CFM brands use the full farm.earthquakeDeductible lookup.
      earthquakeDeductibleOptions: [
        { id: 0, value: 'No coverage' },
        { id: 0.15, value: '15%' }
      ],
      // Brand-restricted farm liability coverage limit dropdowns (read by the liability-coverages
      // form via getMedicalExpenseLimitOptions / getFarmPollutionLimitOptions).
      liabilityCoverageOptions: {
        // #33672: CFM offers only a $10,000 Medical (M) limit — no other selectable options. The
        // farm-utils default already seeds coverageM: 10,000, so this just removes the alternatives.
        medicalExpensePerPerson: [{ id: 10000, value: '$10,000' }],
        // #33674: Limited Farm Pollution — $25,000 default (seeded in data-defaults) with $50,000 and
        // $100,000 options. Premium per limit is priced by CFM's filed ClarionDoor rating tables.
        farmPollution: [
          { id: 25000, value: '$25,000' },
          { id: 50000, value: '$50,000' },
          { id: 100000, value: '$100,000' }
        ]
      },
      liabilityExtensions: {
        rented: {
          [LiabilityType.Personal]: { locations: true, residences: true, structures: true },
          [LiabilityType.Commercial]: { locations: true, residences: true, structures: true }
        },
        occupied: {
          [LiabilityType.Personal]: { locations: true, residences: true },
          // Commercial liability does not extend to occupied locations/residences.
          [LiabilityType.Commercial]: { locations: false, residences: false }
        },
        assets: {
          // #34019: Gator/Utility Transport Vehicle rates like an ATV, so it's enabled wherever ATV is.
          [LiabilityType.Personal]: {
            atv: true,
            gatorUtilityTransportVehicle: true,
            golfCart: true,
            unmannedAircraft: true,
            boat: true
          },
          // Commercial liability does not extend to boats.
          [LiabilityType.Commercial]: {
            atv: true,
            gatorUtilityTransportVehicle: true,
            golfCart: true,
            unmannedAircraft: true,
            boat: false
          }
        }
      },
      /**
       * Policy type masks define how Home (Z) and Rental (Y) differ from full Farm (F).
       * Each mask is a set of overrides — components read from the mask and fall back to default
       * Farm behavior when a value is null/undefined.
       *
       * SOURCE OF TRUTH: The canonical mask config lives in @ourbranch/brand-config-be (cfm.ts).
       * This staff-FE copy must stay in sync. The backend enforces coverageBRule (pricing) plus
       * the risk-type/usage/liability restrictions via post-data-rules; label-type fields are
       * UI-only. Z/Y label/hide/farm-operations fields are at Forreston parity (#32753); the
       * allowedRiskTypes/residenceUsageOverrides allow-lists have not been independently verified
       * against a filing, a formal Robin/Pam sign-off being an open follow-up (issue #31667 "Known
       * gaps" #5 in cfm.ts). The Home and Rental allowedFO6StructureCategories lists were confirmed
       * by stakeholders in #34274 (Home) and #34293 (Rental Solar Panels). Home AND Rental are LIVE
       * for agents in cfm-production as of #34572 — the pre-launch #31043 gate that stripped Z/Y is
       * retired, so all three F/Z/Y masks are present in every environment.
       *
       * coverageBRule { lockedPercent: 0.1 } applies to the F mask too — the spec sheet locks
       * Coverage B at 10% of Coverage A on every policy type.
       */
      policyTypeMasks: {
        // Full Farm — permissive mask except the CFM-specific Coverage B lock
        F: {
          label: 'Farm',
          tabLabel: 'Farm Details',
          addRiskLabel: 'Add a dwelling or structure',
          interestedPartyLabel: 'Additional Interested Party',
          // Coverage B locked at 10% of Coverage A for dwelling risks (CFM spec, issue #31667)
          coverageBRule: { lockedPercent: 0.1 },
          // #34528: Vacant-Rental ('VR') is a Rental-only FO1 usage. The F mask otherwise has no
          // residenceUsageOverrides, so it would fall through to the full base list and surface 'VR'
          // on a Farm dwelling — allow-list the Farm FO1 usages explicitly to keep it Rental-only.
          residenceUsageOverrides: {
            [FarmRiskType.Fo1]: ['P', 'S', 'R', 'V']
          }
        },
        // Home — subset of Farm for owner-occupied / seasonal / vacant homeowner properties
        Z: {
          label: 'Home',
          tabLabel: 'Home Details',
          addRiskLabel: 'Add a structure',
          interestedPartyLabel: 'Additional Insured/Interest',
          allowedRiskTypes: [
            FarmRiskType.Fo1,
            FarmRiskType.Fo2,
            FarmRiskType.Fo3,
            FarmRiskType.Fo4,
            FarmRiskType.Fo6Structures
          ],
          // Residence usage overrides by risk type — remove Rental ('R') from dwelling types
          residenceUsageOverrides: {
            [FarmRiskType.Fo1]: ['P', 'S', 'V'],
            [FarmRiskType.Fo2]: ['P', 'S'],
            [FarmRiskType.Fo3]: ['P', 'S']
          },
          // Mirrors BE cfm.ts: Home (Z) offers Barns/Outbuildings, Fencing, Solar Panels and
          // Dwelling & Contents. Solar Panels ('SP') is MO-only in structureCategoriesByState and
          // CFM writes MO, so it surfaces; Solar and Dwelling & Contents are limit-based structures
          // already fully wired for rating/docs. Rental (Y) also offers Solar Panels but not
          // Dwelling & Contents. (#34274/#34293)
          allowedFO6StructureCategories: [
            StructureCategory.BarnsOutbuildings,
            StructureCategory.Fencing,
            StructureCategory.SolarPanels,
            StructureCategory.DwellingContents
          ],
          // Barns/Outbuildings "Occupancy or use type" restricted to a single "General Use"
          // option — farm-specific uses don't apply to a homeowner structure (mirrors #32485).
          occupancyUseTypeOverrides: {
            [StructureCategory.BarnsOutbuildings]: [{ id: 'LVB', value: 'General Use' }]
          },
          fo6StructuresLabel: 'Other Structures (FO-6)',
          maxDwellingRisks: 1,
          allowedLiabilityTypes: [LiabilityType.Personal, LiabilityType.Commercial],
          // A Home that insures nothing but liability writes Personal only (#34435); once it insures
          // something it may write either. Full Farm (F) offers both regardless; Rental (Y) is
          // Commercial-only.
          liabilityOnlyAllowedLiabilityTypes: [LiabilityType.Personal],
          // Home (Z) shows premises-only liability, not "Commercial" — a homeowner product, not a
          // farm operation. Matches Rental (Y); full Farm (F) keeps the "Commercial Liability (Farm
          // Premises and Operations)" lookups default. Rating id ('C') is unchanged. (#31695, #33064)
          liabilityTypeLabels: {
            [LiabilityType.Personal]: 'Personal Liability Coverage',
            [LiabilityType.Commercial]: 'Premises Liability Coverage'
          },
          // Hide "Primary farm operations" (defaulted to 'O' for the rater) and rename the
          // liability details section (mirrors #31697)
          hideFarmOperations: true,
          liabilityDetailsLabel: 'Home details',
          hiddenLiabilityCoverages: {
            [LiabilityType.Personal]: ['farmPollution', 'employersLiability', 'customFarming'],
            [LiabilityType.Commercial]: [
              'farmPollution',
              'employersLiability',
              'customFarming',
              'customFeeding',
              'incidentalLiability'
            ]
          },
          // GL-4002 (lessor of leased equipment) is not offered on Home (mirrors BE cfm.ts / #32165)
          hiddenInterestedPartyRelationships: {
            [LiabilityType.Personal]: ['lessorOfLeasedEquipment'],
            [LiabilityType.Commercial]: ['lessorOfLeasedEquipment']
          },
          hiddenInlandMarineTypes: null,
          hiddenEndorsements: ['farmExtraExpense'],
          maxAcres: 40,
          disableAddressToggle: false,
          // Coverage B locked at 10% of Coverage A for dwelling risks (mirrors BE cfm.ts)
          coverageBRule: { lockedPercent: 0.1 },
          coverageBLabel: 'Related Private Structures (B)',
          allowedRentedExtensionTypes: ['residences'],
          // Loss Payee interested parties don't schedule farm machinery on Home, so its
          // "Associated equipment" field is dropped (Additional Interest keeps it). (#32521)
          hideLossPayeeEquipment: true
        },
        // Rental — subset of Farm for tenant-occupied rental dwelling properties
        Y: {
          label: 'Rental',
          tabLabel: 'Dwelling Details',
          addRiskLabel: 'Add a structure',
          interestedPartyLabel: 'Additional Insured/Interest',
          allowedRiskTypes: [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo6Structures],
          // Only Rental ('R') usage allowed on dwellings
          residenceUsageOverrides: {
            // #34528: Rental FO1 offers Tenant-Occupied ('R') plus Vacant-Rental ('VR').
            [FarmRiskType.Fo1]: ['R', 'VR'],
            [FarmRiskType.Fo2]: ['R'],
            [FarmRiskType.Fo3]: ['R']
          },
          // Mirrors BE cfm.ts: Rental (Y) offers Barns/Outbuildings, Fencing and Solar Panels (Solar
          // is MO-only, and CFM is MO, so it surfaces). Dwelling & Contents stays Home-only
          // (#34274/#34293).
          allowedFO6StructureCategories: [
            StructureCategory.BarnsOutbuildings,
            StructureCategory.Fencing,
            StructureCategory.SolarPanels
          ],
          occupancyUseTypeOverrides: {
            [StructureCategory.BarnsOutbuildings]: [{ id: 'LVB', value: 'General Use' }]
          },
          fo6StructuresLabel: 'Other Structures (FO-6)',
          // Rental insures a single tenant-occupied dwelling; add-risk.jsx enforces this across all
          // locations, so a 2nd FO-1/2/3 is blocked while dwelling-less locations stay addable.
          // Parity with Home (Z). (#34387)
          maxDwellingRisks: 1,
          // Rental (Y) shows premises-only liability, not "Commercial" — a landlord product, not a
          // farm operation. Matches Home (Z); full Farm (F) keeps the "Commercial Liability (Farm
          // Premises and Operations)" lookups default. Rating id ('C') is unchanged. (#33064)
          allowedLiabilityTypes: [LiabilityType.Commercial],
          liabilityTypeLabels: {
            [LiabilityType.Commercial]: 'Premises Liability Coverage'
          },
          hideFarmOperations: true,
          liabilityDetailsLabel: 'Dwelling details',
          hiddenLiabilityCoverages: {
            [LiabilityType.Commercial]: [
              'farmPollution',
              'employersLiability',
              'customFarming',
              'customFeeding',
              'incidentalLiability',
              // Assets section (ATVs, golf carts, unmanned aircraft) not eligible on rentals
              // (mirrors BE cfm.ts / #32042)
              'additionalAssetsExtensions'
            ]
          },
          // Rental (commercial-liability-only): GL-4002 (lessor of leased equipment), GL-3001
          // (manager/lessor of premises), FO-41 (property coverages), and Landlord do not apply to
          // rentals (#32356). Parity with Forreston Rental.
          hiddenInterestedPartyRelationships: {
            [LiabilityType.Commercial]: [
              'lessorOfLeasedEquipment',
              'managerOrLessorOfPremisesCommercial',
              'propertyCoverages',
              'landlord'
            ]
          },
          // FO-41 (Additional Insureds - Property coverages) does not apply to rentals, so the FO-41
          // add-on toggle is removed from the Additional Insured/Interest section (#32359).
          hideFO41: true,
          // The "Specific activity or location exclusion" toggle is not needed for rentals (#32361).
          hideLiabilityExclusions: true,
          // Boat Dock stays selectable on Rental (rated in Clarion Door) — parity with Forreston
          // Rental (#32075). Confirm CFM's CD model ships the boat-dock rating factor before launch.
          hiddenInlandMarineTypes: [
            'atv',
            // #34019: Gator/Utility Transport Vehicle is hidden on Rental, mirroring ATV.
            'gatorUtilityTransportVehicle',
            'boat',
            'coinCollection',
            'fineArts',
            'furs',
            'golfCart',
            'golfEquipment',
            'guns',
            'jewelry',
            'musicalInstruments',
            'silverware',
            'sportingEquipment',
            'tack',
            'unmannedAircraft'
          ],
          hiddenEndorsements: ['farmExtraExpense', 'identityFraud'],
          maxAcres: 40,
          disableAddressToggle: true,
          // Coverage B locked at 10% of Coverage A, recomputed on every rate (mirrors BE cfm.ts)
          coverageBRule: { lockedPercent: 0.1 },
          coverageBLabel: 'Related Private Structures (B)',
          coverageDLabel: 'Loss of Rent (D)',
          coverageDTooltip:
            'Reimburses lost rental income if the dwelling becomes uninhabitable due to a covered loss, up to the amount entered here.',
          hideExpandedReplacementCost: true,
          allowedRentedExtensionTypes: [],
          // Loss Payee interested parties don't schedule farm machinery on Rental, so its
          // "Associated equipment" field is dropped (Additional Interest keeps it). (#32525)
          hideLossPayeeEquipment: true
        }
      },
      risks: {
        [FarmRiskType.Fo1]: {
          // Mine Subsidence (coverageMS + hasActiveCoverageMS) hidden — CFM does not offer it (GitHub issue #32438).
          hiddenCoverages: ['earthquakeCoverage', 'coverageMS', 'hasActiveCoverageMS'],
          customFormComponentPaths: {
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            roofLossSettlement: 'base/roof-loss-settlement',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            coverageB: 'base/coverage-b',
            lossSettlement: 'base/loss-settlement',
            coverageD: 'FO1/coverage-d',
            replCostMobileHome: 'base/repl-cost-mobile-home',
            // Theft and Vandalism (GO IncludeTheftVandalism; Rental/seasonal default). #36798
            theftVandalism: 'base/theft-vandalism'
          },
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          // Coverage A/C/D limit guardrails — MUST stay in sync with the backend rules in
          // packages/brand-config-be/src/brands/cfm.ts (CFM spec sheet, issue #31667 / PR #32560).
          businessRules: {
            coverageA: {
              // Home, Modular Home (FD), Barndominium: 50-100% ACV
              acvBasedStructures: {
                propertyTypes: ['H', 'FD', 'B'],
                minPercent: 0.5,
                maxPercent: 1.0
              },
              // Mobile Home: $7/sqft minimum, max 100% ACV
              mobileHome: {
                propertyTypes: ['MH'],
                minPerSqFt: 7,
                maxPercent: 1.0
              }
            },
            coverageC: {
              byResidenceUsage: {
                P: { min: 0, max: 'coverageA' }, // Primary
                S: { min: 0, max: 'coverageA' }, // Seasonal
                R: { min: 0, max: 25000 }, // Tenant Occupied Rental
                V: { min: 0, max: 'coverageA' }, // Vacant
                VR: { min: 0, max: 'coverageA' } // Vacant-Rental — same as Vacant (#34528)
              }
            },
            // Coverage D is a plain editable dollar amount, $0 default up to $3,000, for
            // Primary/Seasonal/Tenant (sheet: "$0—ability to write value up to $3K"; no locked
            // percent-of-Coverage-A split). Vacant is locked at $0 (#33149) — a vacant dwelling has
            // no additional living cost. MUST mirror brand-config-be cfm.ts (asserted in cfm.test.ts).
            coverageD: {
              byResidenceUsage: {
                P: { min: 0, max: 3000, allowEditing: true },
                S: { min: 0, max: 3000, allowEditing: true },
                R: { min: 0, max: 3000, allowEditing: true },
                V: { min: 0, max: 0, allowEditing: false },
                VR: { min: 0, max: 0, allowEditing: false } // Vacant-Rental — no Loss of Rent, $0 like Vacant (#34528)
              }
            },
            deductibleAllOther: {
              minByCoverageA: cfmMinAllPerilDeductibleByCoverageA
            }
          }
        },
        [FarmRiskType.Fo2]: {
          hiddenCoverages: ['coverageMS', 'hasActiveCoverageMS'],
          customFormComponentPaths: {
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            coverageB: 'base/coverage-b',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            roofLossSettlement: 'base/roof-loss-settlement',
            coverageD: 'FO2/coverage-d',
            replCostMobileHome: 'base/repl-cost-mobile-home',
            // Theft and Vandalism (GO IncludeTheftVandalism; Rental/seasonal default). #36798
            theftVandalism: 'base/theft-vandalism',
            coverageWaterBackup: 'base/coverage-water-backup'
          },
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          // Coverage A/C/D limit guardrails — MUST stay in sync with the backend rules in
          // packages/brand-config-be/src/brands/cfm.ts (CFM spec sheet, issue #31667 / PR #32560).
          businessRules: {
            coverageA: {
              acvBasedStructures: {
                propertyTypes: ['H', 'FD', 'B'],
                minPercent: 0.5,
                maxPercent: 1.0
              },
              mobileHome: {
                propertyTypes: ['MH'],
                // $6/sqft minimum.
                minPerSqFt: 6,
                maxPercent: 1.0
              },
              rceBasedStructures: {
                minPercent: 0.8,
                maxPercent: 1.0,
                // #35312: dwellings built in 1950 or earlier settle on Functional Replacement Cost
                // terms and floor at 80% of RCE, the same as every other FO-2 RC dwelling. The old
                // 50% carve-out survives only as the grandfathered floor for terms written under it.
                yearBuiltOverrides: {
                  threshold: 1950,
                  minPercentAtOrBefore: 0.8,
                  grandfatheredMinPercentAtOrBefore: 0.5
                }
              }
            },
            // #33150: Coverage C split by loss settlement (mirroring brand-config-be cfm.ts). ACV
            // Primary/Seasonal floor at $0 (can be decreased to $0); RC Primary/Seasonal keep the
            // 50%-of-Coverage-A floor. The Vacant guard is identical on both paths. (Vacant is
            // FO1-only per the spec, but the $0-only guard stays for Farm (F) policies where the
            // mask doesn't restrict residence usage.) #33264: ACV Tenant seeds at $5,000
            // (defaultValue) but can decrease to $0 and increase to $25,000; RC Tenant keeps its
            // $5,000 floor. Both cap at $25,000.
            coverageC: {
              byLossSettlement: {
                ACV: {
                  byResidenceUsage: {
                    P: { min: 0, max: 'coverageA' },
                    S: { min: 0, max: 'coverageA' },
                    R: { min: 0, max: 25000, defaultValue: 5000 }, // #33264: seed $5K, floor $0, cap $25K
                    V: { min: 0, max: 0 }
                  }
                },
                RC: {
                  byResidenceUsage: {
                    P: { minPctOfCoverageA: 0.5, max: 'coverageA' },
                    S: { minPctOfCoverageA: 0.5, max: 'coverageA' },
                    R: { min: 5000, max: 25000 },
                    V: { min: 0, max: 0 }
                  }
                }
              }
            },
            // #33149: Coverage D. ACV Primary/Seasonal/Tenant are editable $0–$3,000, seeded at
            // $3,000 (was locked at 20% of Coverage A). RC Primary/Seasonal stay locked at 20% of
            // Coverage A; RC Tenant seeds $3,000, floored at $3,000, no upper cap ("increase to any
            // amount"). Vacant locked $0 on both paths. Mirrors brand-config-be cfm.ts — must stay
            // in sync (see cfm.test.js #33232): the FO2 form's locked-Coverage-D sync effect reads
            // these rules to decide editability, and a stale locked rule silently overwrote the
            // editable $3,000 with 20% of Coverage A, leaving the offer permanently dirty.
            coverageD: {
              byLossSettlement: {
                ACV: {
                  byResidenceUsage: {
                    P: { min: 0, max: 3000, allowEditing: true, defaultValue: 3000 },
                    S: { min: 0, max: 3000, allowEditing: true, defaultValue: 3000 },
                    R: { min: 0, max: 3000, allowEditing: true, defaultValue: 3000 },
                    V: { min: 0, max: 0, allowEditing: false }
                  }
                },
                RC: {
                  byResidenceUsage: {
                    P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    R: { min: 3000, allowEditing: true, defaultValue: 3000 },
                    V: { min: 0, max: 0, allowEditing: false }
                  }
                }
              }
            },
            deductibleAllOther: {
              minByCoverageA: cfmMinAllPerilDeductibleByCoverageA
            }
          }
        },
        [FarmRiskType.Fo3]: {
          hiddenCoverages: [
            'coverageMS',
            'hasActiveCoverageMS',
            'expandedDebrisRemoval',
            'businessProperty',
            'sprinklerLeakage',
            // unscheduled personal property
            'moneyLimit',
            'jewelryLimit',
            'securitiesLimit',
            'motorizedVehiclesLimit',
            'silverwareLimit',
            'gunsLimit',
            'businessPropertyLimit',
            'windHailExclusion'
          ],
          customFormComponentPaths: {
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            roofLossSettlement: 'base/roof-loss-settlement',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            coverageB: 'base/coverage-b',
            coverageD: 'FO3/coverage-d',
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            repairRebuildRequirementCalled: 'FO3/repair-rebuild-requirement',
            personalPropertyReplCost: 'FO3/personal-property-repl-cost',
            lossSettlement: 'base/loss-settlement',
            replCostMobileHome: 'base/repl-cost-mobile-home',
            // Theft and Vandalism (GO IncludeTheftVandalism; Rental/seasonal default). #36798
            theftVandalism: 'base/theft-vandalism',
            coverageWaterBackup: 'base/coverage-water-backup'
          },
          allowedOptions: {
            constructionTypeExemptPropertyTypes: ['MH'],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          // Coverage A/C/D limit guardrails (issues #28684, #29011, #29013, #30548).
          // FO3 is always RC loss settlement.
          businessRules: {
            coverageA: {
              rceBasedStructures: {
                minPercent: 0.9,
                maxPercent: 1.0
              }
            },
            coverageC: {
              byResidenceUsage: {
                P: { minPctOfCoverageA: 0.75, max: 'coverageA' },
                S: { minPctOfCoverageA: 0.75, max: 'coverageA' },
                R: { min: 5000, max: 25000 },
                V: { min: 0, max: 0 }
              }
            },
            // #33149: Primary/Seasonal stay locked at 20% of Coverage A. Tenant seeds $3,000,
            // floored at $3,000, and can be increased to any amount (no upper cap — `max` omitted);
            // the old locked 10% of Coverage A produced a negative premium in ClarionDoor. Mirrors
            // brand-config-be cfm.ts — must stay in sync (see cfm.test.js #33232), else the FO3
            // Tenant offer hits the same permanent-dirty loop as FO2.
            coverageD: {
              byResidenceUsage: {
                P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                R: { min: 3000, allowEditing: true, defaultValue: 3000 },
                V: { min: 0, max: 0, allowEditing: false }
              }
            },
            deductibleAllOther: {
              minByCoverageA: cfmMinAllPerilDeductibleByCoverageA
            }
          }
        },
        [FarmRiskType.Fo4]: {
          hiddenCoverages: [
            'expandedDebrisRemoval',
            'moneyLimit',
            'jewelryLimit',
            'securitiesLimit',
            'motorizedVehiclesLimit',
            'silverwareLimit',
            'gunsLimit',
            'businessPropertyLimit',
            'mineSubsidenceCoverage'
          ],
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          customFormComponentPaths: {
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            deductibleEarthquake: 'base/deductible-earthquake',
            replCostMobileHome: 'base/repl-cost-mobile-home',
            coverageWaterBackup: 'base/coverage-water-backup'
          }
        },
        [FarmRiskType.Fo6Scheduled]: {
          // Coverage F earthquake is offered on FO6-scheduled for the Farm (F) product only —
          // Home (Z) and Rental (Y) don't include Fo6Scheduled in their allowedRiskTypes, so
          // there's no need to gate this per policy-type mask (issue #32439).
          hiddenCoverages: ['expandedDebrisRemoval', 'hasSnowWeightCoverage'],
          coverageF: {
            // CFM spec sheet (issue #31667) includes "Rented or borrowed equipment" (12) and
            // "Chemicals, Seed and Miscellaneous" (14).
            // 12/14 use the generic item form (no itemOverrides entry): QA should confirm the
            // generic fields (description, limit, deductible) suffice for them.
            itemTypes: scheduledFarmPropertyCategories.filter((option) => !['10', '11', '13'].includes(option.id)),
            // #33662: $1,000 is the default all-peril deductible on Coverage F items (livestock uses
            // its own $250 default below).
            deductibleOptions: [
              { id: 1000, value: '$1,000' },
              { id: 1500, value: '$1,500' },
              { id: 2500, value: '$2,500' },
              { id: 5000, value: '$5,000' },
              { id: 10000, value: '$10,000' },
              { id: 20000, value: '$20,000' }
            ],
            livestockDeductibleOptions: [
              { id: 250, value: '$250' },
              { id: 500, value: '$500' },
              { id: 1000, value: '$1,000' },
              { id: 1500, value: '$1,500' },
              { id: 2500, value: '$2,500' },
              { id: 5000, value: '$5,000' },
              { id: 10000, value: '$10,000' },
              { id: 20000, value: '$20,000' }
            ],
            // #33662: $1,000 default for all Coverage F items except livestock, which defaults to $250.
            defaultDeductible: 1000,
            defaultLivestockDeductible: 250,
            showWindHailDeductible: true,
            // CFM floors the calculated wind/hail deductible amount at $2,500 (issue #32483, #33662).
            windHailDeductibleFloor: 2500,
            // #33662: per-item all-peril deductible minimums enforced by the farm-shared validation
            // schema (validateCoverageFItemDeductible). An item valued over $500,000 requires at least
            // a $2,500 deductible; electing the Special Cattle Endorsement requires at least $1,000.
            minDeductibleByValue: [{ minValue: 500000, minDeductible: 2500 }],
            specialCattleEndorsementMinDeductible: 1000,
            itemOverrides: {
              1: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                hideReplacementCost: true,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              2: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              3: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                hideReplacementCost: true,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              4: { descriptionLabel: 'Description' },
              5: { descriptionLabel: 'Description', hideIncreaseStackLimit: true },
              6: {
                descriptionLabel: 'Description (Make/Model)',
                hideReplacementCost: true,
                hideOpenPerils: true,
                hideMakeField: true
              },
              7: {
                descriptionLabel: 'Description',
                winterPerilsAlwaysOn: true,
                hideSuffocationOfLivestock: true,
                hideAdditionalPerils: true
              },
              8: { descriptionLabel: 'Description', hideMakeField: true, hideSerialNumber: true },
              9: { descriptionLabel: 'Description', hideMakeField: true }
            }
          },
          allowedOptions: {
            peakSeasonItemTypes: scheduledFarmPropertyCategories.filter((option) =>
              ['4', '5', '14'].includes(option.id)
            ),
            scheduledCoverageTypes: [
              FarmScheduleCoverageType.SPP,
              FarmScheduleCoverageType.PEAK,
              FarmScheduleCoverageType.INCOME
            ],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          customFormComponentPaths: {
            deductibleEarthquake: 'base/deductible-earthquake'
          }
        },
        [FarmRiskType.Fo6Unscheduled]: {
          hiddenCoverages: ['hasSnowWeightCoverage'],
          allowedOptions: {
            scheduledCoverageTypes: [FarmScheduleCoverageType.SPP],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          coverageG: {
            deductibleOptions: allOtherOptions.filter((option) => option.id >= 1000),
            defaultDeductible: 1000
          },
          customFormComponentPaths: {
            deductibleWindHail: 'FO6Unscheduled/deductible-wind-hail',
            deductibleEarthquake: 'base/deductible-earthquake'
          }
        },
        [FarmRiskType.InlandMarine]: {
          // Standalone inland marine (#33164): a building-less risk whose only content is its SPP
          // scheduleCoverages items, so SPP is the single scheduled coverage type it offers and it
          // has no coverage fields of its own (hence no hiddenCoverages / customFormComponentPaths).
          // Item types and deductibles intentionally match the Fo6Unscheduled block above — the same
          // CFM-filed inland marine options. This entry is not optional in practice: InlandMarineForm
          // reads appConfig.farm[state].risks[riskType] directly, so without it the card would fall
          // back to the repo-wide default item types and the full inlandMarineDeductible lookup
          // rather than CFM's filed subset.
          allowedOptions: {
            scheduledCoverageTypes: [FarmScheduleCoverageType.SPP],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          }
        },
        [FarmRiskType.Fo6Structures]: {
          hiddenCoverages: ['hasEarthquakeExteriorVeneerCoverage', 'coverageMS', 'hasActiveCoverageMS'],
          // CFM floors the FO-6 structure wind/hail deductible at $2,500 (issue #32483). Consumed by the
          // base deductible-wind-hail component and pre-applied into the offer form's initialValues so a
          // sub-floor stored/seeded value doesn't leave the offer dirty and hide "Send to underwriting"
          // after the UW-review Save (#35186). Mirrors the coverageF windHailDeductibleFloor above.
          windHailDeductibleFloor: 2500,
          allowedOptions: {
            constructionTypes: allConstructionTypeIds.filter((id) => id !== 'MH'),
            scheduledCoverageTypes: [
              FarmScheduleCoverageType.SPP,
              FarmScheduleCoverageType.INCOME
              // Note: RELATED_PRIVATE_STRUCTURES intentionally excluded
            ],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: cfmInlandMarineDeductibleOptions
          },
          // FO6Structures rules (issues #28684, #28945, #28950, #28923):
          // building-class/confinement/poultry age caps and Coverage E RC/ACV percent bands.
          businessRules: {
            buildingClass1MaxAgeYears: 25,
            confinementBuildingMaxAgeYears: 15,
            poultryBuildingMaxAgeYears: 15,
            coverageE: {
              rceBasedStructures: {
                minPercent: 0.8, // 80% of RCE minimum
                maxPercent: 1.0 // 100% of RCE maximum
              },
              acvBasedStructures: {
                minPercent: 0.8 // 80% of ACV minimum; max is 100% RCE
              }
            }
          },
          customFormComponentPaths: {
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            deductibleEarthquake: 'base/deductible-earthquake',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            roofLossSettlement: 'base/roof-loss-settlement'
          }
        }
      }
    }
  },
  agencyPortalRoutes: [
    {
      route: 'updates',
      src: 'https://portal.ourbranch.com/ebd/160113b696aa823a9219015f4b8cdca6',
      title: 'News and Updates',
      icon: 'portal'
    },
    {
      route: 'reports',
      src: 'https://portal.ourbranch.com/ebd/ccd113b696aa8321a97c01713c7b22a9',
      title: 'Reports & Commissions',
      icon: 'pieChart'
    },
    {
      route: 'underwriting',
      src: 'https://portal.ourbranch.com/ebd/e9b113b696aa826bbfe101d91ddbd240',
      title: 'Underwriting',
      icon: 'pen'
    },
    {
      route: 'tools',
      src: 'https://portal.ourbranch.com/ebd/9a3113b696aa8273ba9d014fc744fd8b',
      title: 'Tools & Resources',
      icon: 'tool'
    },
    {
      route: 'training',
      src: 'https://portal.ourbranch.com/ebd/17c113b696aa82f381c68162f599b752',
      title: 'Training',
      icon: 'puzzlePiece'
    },
    {
      route: 'claims',
      src: 'https://portal.ourbranch.com/ebd/939113b696aa8245a08701ab46589468',
      title: 'Claims',
      icon: 'claim'
    },
    {
      route: 'contact-us',
      src: 'https://portal.ourbranch.com/ebd/58c113b696aa83558f3381d6b96a5685',
      title: 'Contact',
      icon: 'contact'
    }
  ]
};

// Full Farm (F), Home (Z), and Rental (Y) are ALL live in production as of #34572: CFM Homeowners
// and Rental are activated for agents effective 9/1/2026; farmMinimumEffectiveDate (Z/Y share
// global.farmEffectiveDate with Farm) enforces the 9/1 binding floor. No sub-type is stripped from
// `policyTypeMasks` in production anymore — the pre-launch #31043 gate that removed Z/Y in
// `cfm-production` has been retired. If a sub-type ever needs gating again, restore an
// isProductionEnv() strip here AND in the backend source of truth (@ourbranch/brand-config-be/cfm.ts)
// in the same release.

export default config;
